/**
 * Deterministic mock compliance history per resident (Phase 1 — date-based history).
 * Used for dietary changes, care plan versions, MAR-style logs, and assessment trends.
 * Persisted quick-form instances (localStorage) are merged into assessment trends.
 */

import { getInstancesForResident } from './clinicalAssessmentInstances';

const monthsAgo = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * @param {number|string} residentId
 * @returns {{
 *   dietaryHistory: Array<{effectiveDate: string, summary: string, iddsiFood?: string, iddsiFluid?: string}>,
 *   carePlanVersions: Array<{version: string, effectiveDate: string, summary: string, author: string}>,
 *   medicationAdministrationLog: Array<{dateTime: string, medication: string, route: string, status: string, staff: string}>,
 *   assessmentTrends: Array<{date: string, assessment: string, score: string, outcome: string}>
 * }}
 */
export function buildComplianceHistory(residentId) {
  const id = Number(residentId) || 1;
  const offset = id % 5;

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[complianceHistory] buildComplianceHistory', { residentId: id });
  }

  return {
    dietaryHistory: [
      {
        effectiveDate: monthsAgo(6 + offset),
        summary: 'Regular diabetic diet — admission',
        iddsiFood: 'Level 6 (Soft & bite-sized)',
        iddsiFluid: 'Level 3 (Moderately thick)',
      },
      {
        effectiveDate: monthsAgo(3),
        summary: 'IDDSI texture review — speech pathology',
        iddsiFood: 'Level 5 (Minced & moist)',
        iddsiFluid: 'Level 3',
      },
      {
        effectiveDate: monthsAgo(1),
        summary: 'Quarterly nutrition review — no change to regime',
        iddsiFood: 'Level 5',
        iddsiFluid: 'Level 3',
      },
    ],

    carePlanVersions: [
      {
        version: `CP-${id}-v1.0`,
        effectiveDate: monthsAgo(8),
        summary: 'Initial person-centred plan from admission assessments',
        author: 'RN Sarah Johnson',
      },
      {
        version: `CP-${id}-v1.1`,
        effectiveDate: monthsAgo(4),
        summary: '8-weekly review — goals updated (mobility, falls)',
        author: 'RN Sarah Johnson',
      },
      {
        version: `CP-${id}-v2.0`,
        effectiveDate: monthsAgo(1),
        summary: 'Major review — AN-ACC class change reflected',
        author: 'Care Manager Mike Chen',
      },
    ],

    medicationAdministrationLog: [
      {
        dateTime: `${daysAgo(0)} 08:00`,
        medication: 'Metformin 500mg',
        route: 'Oral',
        status: 'Given',
        staff: 'RN A. Patel',
      },
      {
        dateTime: `${daysAgo(0)} 20:00`,
        medication: 'Metformin 500mg',
        route: 'Oral',
        status: 'Given',
        staff: 'EN K. Lee',
      },
      {
        dateTime: `${daysAgo(1)} 08:00`,
        medication: 'Lisinopril 10mg',
        route: 'Oral',
        status: 'Given',
        staff: 'RN A. Patel',
      },
      {
        dateTime: `${daysAgo(1)} 14:00`,
        medication: 'Paracetamol 500mg (PRN)',
        route: 'Oral',
        status: 'Given — mild pain 3/10',
        staff: 'EN K. Lee',
      },
      {
        dateTime: `${daysAgo(2)} 08:00`,
        medication: 'Vitamin D 1000IU',
        route: 'Oral',
        status: 'Refused — unwell',
        staff: 'RN J. Smith',
      },
    ],

    assessmentTrends: (() => {
      const mockTrends = [
        {
          date: monthsAgo(6),
          assessment: 'Falls risk (Morse)',
          score: `${6 + offset}`,
          outcome: 'Low risk',
        },
        {
          date: monthsAgo(3),
          assessment: 'Braden (pressure)',
          score: `${18 + (offset % 3)}`,
          outcome: 'No risk',
        },
        {
          date: monthsAgo(3),
          assessment: 'MMSE',
          score: `${22 + (offset % 4)}`,
          outcome: 'Mild cognitive impairment',
        },
        {
          date: daysAgo(14),
          assessment: 'Pain (NRS)',
          score: '2/10',
          outcome: 'Mild — PRN available',
        },
      ];
      const fromSaved = getInstancesForResident(residentId).map((row) => {
        const c = row.card;
        if (!c) return null;
        return {
          date: c.date || (row.savedAt || '').slice(0, 10),
          assessment: c.name || 'Assessment',
          score: c.score != null ? String(c.score) : '—',
          outcome: c.status || 'Recorded (demo)',
        };
      }).filter(Boolean);
      const merged = [...mockTrends, ...fromSaved];
      merged.sort((a, b) => String(b.date).localeCompare(String(a.date)));
      return merged;
    })(),
  };
}
